import { useEffect } from 'react';
import { SearchModal, useSearchModal } from '@backstage/plugin-search';
import { isMacPlatform } from '../nav/isMacPlatform';
import { HoneSearchPalette } from './HoneSearchPalette';

/*
 * The one search modal instance for the whole shell.
 *
 * `SidebarSearchModal` bundles its own trigger, provider and dialog, which
 * confines the dialog's open state to the sidebar. Hoisting the dialog here —
 * under the `SearchModalProvider` in `layoutModule` — lets any shell affordance
 * (the sidebar trigger, the header, the keyboard) open the same instance
 * through `useSearchModal`.
 *
 * Also owns the Cmd+K / Ctrl+K binding the sidebar trigger's key hint
 * advertises — `isMacPlatform` decides which modifier this listens for, the
 * same check the badge reads, so the two can't disagree.
 * `setOpen(true)` rather than `toggleModal` so a repeated press on an open
 * dialog is a no-op instead of closing it.
 *
 * The body is `HoneSearchPalette`, passed through `SearchModal`'s `children`
 * render prop. The vendor body cannot reach the mock by CSS — it never renders
 * the grouped rows, the key hints or the footer — but the shell around it is
 * worth keeping: the `Dialog` (whose `aria-label="Search Modal"` the theme's
 * paper and overlay rules key off) and the `SearchContextProvider` the palette
 * reads through `useSearch`.
 */
export function PortalSearchModal() {
  const { state, toggleModal, setOpen } = useSearchModal();

  useEffect(() => {
    const modifierPressed = isMacPlatform()
      ? (event: KeyboardEvent) => event.metaKey
      : (event: KeyboardEvent) => event.ctrlKey;
    const onKeyDown = (event: KeyboardEvent) => {
      if (modifierPressed(event) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [setOpen]);

  return (
    <SearchModal {...state} toggleModal={toggleModal}>
      {({ toggleModal: close }) => <HoneSearchPalette toggleModal={close} />}
    </SearchModal>
  );
}
